import React, { useCallback, useRef, useState } from 'react';

import { alpha, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  Circle,
  CircleCheck,
  CirclePause,
  CircleX,
  ListChecks,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useClickAway } from 'react-use';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, AlineTooltip, Spacer } from 'src/components/generic';
import { DragIcon } from 'src/components/icons/DragIcon';
import { StartedColumnIcon } from 'src/components/icons/StartedColumnIcon';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  ColorPickerPopover,
  DEFAULT_COLUMN_COLOR,
  getDefaultColumnColor,
} from './ColorPickerPopover';
import type { Column } from './helpers';

type ColumnIcon = React.ComponentType<{ color?: string; size?: number }>;

function StartedColumnIconAdapter({ color, size }: { color?: string; size?: number }) {
  return <StartedColumnIcon fill={color} height={size} width={size} />;
}

function getColumnIcon(category: Apollo.ColumnCategory): ColumnIcon {
  switch (category) {
    case Apollo.ColumnCategory.Done:
      return CircleCheck;
    case Apollo.ColumnCategory.Paused:
      return CirclePause;
    case Apollo.ColumnCategory.Cancelled:
      return CircleX;
    case Apollo.ColumnCategory.Started:
      return StartedColumnIconAdapter;
    case Apollo.ColumnCategory.Triage:
      return ListChecks;
    case Apollo.ColumnCategory.NotStarted:
    default:
      return Circle;
  }
}

/** Width of the gutter the drag handle occupies, just outside the row's own box. */
const DRAG_HANDLE_GUTTER_PX = 24;

// Shared styles used across components
const useSharedStyles = makeStyles()(theme => ({
  root: {
    '&:hover': {
      // Neutral rather than theme.palette.action.hover, which is a blue tint.
      backgroundColor: alpha(theme.appColors.iconsType, 0.05),
      borderRadius: theme.shape.borderRadius,
    },
    alignItems: 'center',
    cursor: 'grab',
    display: 'flex',
    minHeight: 56,
    padding: theme.spacing(1, 1.5),
    // Anchors the drag handle, which hangs in the gutter to the left of the row.
    position: 'relative',
    transition: 'background-color 0.2s',
  },
  rootEditing: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    cursor: 'default',
  },
  rootNotDraggable: {
    cursor: 'default',
  },
  rootStatic: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

interface ColumnItemEditProps {
  column: Column | null;
  /** 1-based position within the category group */
  columnIndex: number;
  editedColumn: Partial<Column>;
  inputRef: React.Ref<HTMLInputElement>;
  onCancel: () => void;
  onColorChange: (color: string) => void;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  totalColumns: number;
  /** Category fallback when column is null (new column creation) */
  category?: Apollo.ColumnCategory;
}

const useColumnItemEditStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  editContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(2),
  },
}));

function ColumnItemEdit({
  category,
  column,
  columnIndex,
  editedColumn,
  inputRef,
  onCancel,
  onColorChange,
  onNameChange,
  onSave,
  totalColumns,
}: ColumnItemEditProps): JSX.Element {
  const { classes: sharedClasses } = useSharedStyles();
  const { classes } = useColumnItemEditStyles();
  const editContainerRef = useRef<HTMLDivElement>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);

  const canSave = Boolean(editedColumn.name?.trim());

  const handleSave = () => {
    if (canSave) onSave();
  };

  useClickAway(editContainerRef, e => {
    // Ignore clicks on portaled popover content (color picker)
    if (e.target instanceof Node) {
      const popoverRoot = document.querySelector('.MuiPopover-root');
      if (popoverRoot?.contains(e.target)) return;
    }
    // Auto-save on click away if valid, otherwise cancel
    if (canSave) {
      onSave();
    } else {
      onCancel();
    }
  });

  return (
    <>
      <div ref={editContainerRef} className={clsx(sharedClasses.root, sharedClasses.rootEditing)}>
        <div className={classes.editContainer}>
          <ProgressiveColumnIcon
            category={column?.category ?? category}
            color={editedColumn.color}
            index={columnIndex}
            total={totalColumns}
            onIconClick={e => setColorPickerAnchor(e.currentTarget)}
          />
          <AlineInput
            ref={inputRef}
            fullWidth
            placeholder="Type column name..."
            value={editedColumn.name}
            onChange={onNameChange}
            onKeyDown={e => {
              if (e.key === 'Escape') onCancel();
              if (e.key === 'Enter') handleSave();
            }}
          />
        </div>
        <div className={classes.actions}>
          <AlineButton color="primary" variant="outlined" onClick={onCancel}>
            Cancel
          </AlineButton>
          <AlineButton color="primary" disabled={!canSave} variant="contained" onClick={handleSave}>
            Save
          </AlineButton>
        </div>
      </div>
      <ColorPickerPopover
        anchorEl={colorPickerAnchor}
        open={Boolean(colorPickerAnchor)}
        selectedColor={editedColumn.color}
        onClose={() => setColorPickerAnchor(null)}
        onColorSelect={onColorChange}
      />
    </>
  );
}

interface ClickableColumnIconProps {
  /** Size of the square the glyph is centered in — drives the row/chip height. */
  boxSize?: number;
  category?: Apollo.ColumnCategory;
  className?: string;
  color?: string | null;
  iconSize?: number;
  onIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  showBackground?: boolean;
  showBorder?: boolean;
}

const DEFAULT_ICON_BOX_SIZE = 32;

interface ClickableColumnIconStyleProps {
  boxSize: number;
  isClickable: boolean;
  showBorder: boolean;
}

/**
 * Swallowing the click is only right when the icon does something with it —
 * an icon with no handler has to stay part of whatever hit area it sits in.
 */
function getIconClickHandler(onIconClick: (e: React.MouseEvent<HTMLDivElement>) => void) {
  return (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onIconClick(e);
  };
}

const useClickableColumnIconStyles = makeStyles<ClickableColumnIconStyleProps>()(
  (theme, { boxSize, isClickable, showBorder }) => ({
    root: {
      '&:hover': isClickable ? { border: `1px solid ${theme.appColors.rule}` } : {},
      alignItems: 'center',
      border: showBorder ? `1px solid ${theme.appColors.rule}` : '1px solid transparent',
      borderRadius: 4,
      cursor: isClickable ? 'pointer' : 'default',
      display: 'flex',
      fontSize: theme.typography.pxToRem(16),
      height: boxSize,
      justifyContent: 'center',
      minWidth: boxSize,
      transition: 'border-color 0.2s, transform 0.2s',
      width: boxSize,
    },
  })
);

export function ClickableColumnIcon({
  boxSize = DEFAULT_ICON_BOX_SIZE,
  category,
  className,
  color,
  iconSize = 24,
  onIconClick,
  showBackground = true,
  showBorder = true,
}: ClickableColumnIconProps): JSX.Element {
  const { classes } = useClickableColumnIconStyles({
    boxSize,
    isClickable: !!onIconClick,
    showBorder,
  });
  const IconComponent = category ? getColumnIcon(category) : Circle;
  const iconColor = color ?? (category ? getDefaultColumnColor(category) : undefined);
  const bgAlpha = showBorder ? 0.1 : 0.05;

  return (
    <div
      className={clsx(className, classes.root)}
      style={{ backgroundColor: showBackground && color ? alpha(color, bgAlpha) : undefined }}
      onClick={onIconClick ? getIconClickHandler(onIconClick) : undefined}
    >
      <IconComponent color={iconColor} size={iconSize} />
    </div>
  );
}

/**
 * Generate SVG arc path for the progress pie slice inside the ring.
 * Viewbox is 0-100; the pie sits inside the outline ring (drawn separately).
 * @param index - Current position (1-based)
 * @param total - Total number of items in group
 */
function getProgressPath(index: number, total: number): string {
  const radius = 28;
  const center = 50;
  // Treat "Done" as the implicit state beyond the last Started column:
  // dividing by (total + 1) guarantees the last Started column is
  // strictly less than a full circle, keeping Started visually distinct
  // from Done regardless of how many Started columns exist.
  const percent = index / (total + 1);

  // Calculate end point (starting from top -90 degrees)
  const angle = percent * 360 - 90;
  const rad = (angle * Math.PI) / 180;

  const x = center + radius * Math.cos(rad);
  const y = center + radius * Math.sin(rad);

  // Large arc flag is 1 if more than 50%
  const largeArcFlag = percent > 0.5 ? 1 : 0;

  return `M ${center} ${center} L ${center} ${center - radius} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x} ${y} Z`;
}

interface ProgressiveColumnIconProps {
  /** Current position in the group (1-based) */
  index: number;
  /** Total number of columns in the group */
  total: number;
  /** Size of the square the glyph is centered in — drives the row/chip height. */
  boxSize?: number;
  category?: Apollo.ColumnCategory;
  className?: string;
  color?: string | null;
  iconSize?: number;
  onIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  showBackground?: boolean;
  showBorder?: boolean;
}

const useProgressiveColumnIconStyles = makeStyles<ClickableColumnIconStyleProps>()(
  (theme, { boxSize, isClickable, showBorder }) => ({
    progressSvg: {
      display: 'block',
    },
    root: {
      '&:hover': isClickable ? { border: `1px solid ${theme.appColors.rule}` } : {},
      alignItems: 'center',
      border: showBorder ? `1px solid ${theme.appColors.rule}` : '1px solid transparent',
      borderRadius: 4,
      cursor: isClickable ? 'pointer' : 'default',
      display: 'flex',
      height: boxSize,
      justifyContent: 'center',
      minWidth: boxSize,
      transition: 'border-color 0.2s, transform 0.2s',
      width: boxSize,
    },
  })
);

export function ProgressiveColumnIcon({
  boxSize = DEFAULT_ICON_BOX_SIZE,
  category,
  className,
  color,
  iconSize = 24,
  index,
  onIconClick,
  showBackground = true,
  showBorder = false,
  total,
}: ProgressiveColumnIconProps): JSX.Element {
  const { classes } = useProgressiveColumnIconStyles({
    boxSize,
    isClickable: !!onIconClick,
    showBorder,
  });
  const isStartedCategory = category === Apollo.ColumnCategory.Started;
  /**
   * Show progressive icon ONLY for Started category.
   * The last Started column renders as a near-full pie; 100% is reserved
   * for the Done icon so the two states remain visually distinguishable.
   */
  const shouldShowProgressiveIcon = isStartedCategory && total > 0;

  if (!shouldShowProgressiveIcon) {
    return (
      <ClickableColumnIcon
        boxSize={boxSize}
        category={category}
        className={className}
        color={color}
        iconSize={iconSize}
        showBackground={showBackground}
        showBorder={showBorder}
        onIconClick={onIconClick}
      />
    );
  }

  const fillColor = color || '#E87A14';
  const bgAlpha = showBorder ? 0.1 : 0.05;

  return (
    <div
      className={clsx(className, classes.root)}
      style={{ backgroundColor: showBackground ? alpha(fillColor, bgAlpha) : undefined }}
      onClick={onIconClick ? getIconClickHandler(onIconClick) : undefined}
    >
      <svg
        className={classes.progressSvg}
        fill="none"
        height={iconSize}
        viewBox="0 0 100 100"
        width={iconSize}
      >
        {/* Outline ring */}
        <circle cx="50" cy="50" fill="none" r="36" stroke={fillColor} strokeWidth={7} />
        {/* Progress pie slice inside the ring */}
        <path d={getProgressPath(index, total)} fill={fillColor} />
      </svg>
    </div>
  );
}

interface ColumnItemViewProps {
  column: Column | null;
  /** 1-based position within the category group */
  columnIndex: number;
  onColorChange: (color: string) => void;
  totalColumns: number;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isDragDisabled?: boolean;
  isLocked?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
}

const useColumnItemViewStyles = makeStyles<void, 'dragHandleWrapper' | 'hoverActions'>()(
  (theme, _params, classes) => ({
    columnIconContainer: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'center',
    },
    dragHandle: {
      color: theme.palette.text.secondary,
      height: 16,
      width: 16,
    },
    /**
     * Sits in the gutter rather than in the row so revealing it on hover doesn't
     * shift the row's content. Hovering it still counts as hovering the row,
     * since :hover applies to the DOM parent regardless of where the child sits.
     */
    dragHandleWrapper: {
      alignItems: 'center',
      bottom: 0,
      cursor: 'grab',
      display: 'flex',
      justifyContent: 'center',
      left: -DRAG_HANDLE_GUTTER_PX,
      opacity: 0,
      position: 'absolute',
      top: 0,
      transition: 'opacity 0.2s',
      width: DRAG_HANDLE_GUTTER_PX,
    },
    hoverActions: {
      display: 'flex',
      gap: theme.spacing(0.5),
      marginLeft: 'auto',
      opacity: 0,
      transition: 'opacity 0.2s',
    },
    name: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 500,
      marginBottom: theme.spacing(0.25),
    },
    rootWithHover: {
      [`&:hover .${classes.dragHandleWrapper}, &:hover .${classes.hoverActions}`]: {
        opacity: 1,
      },
    },
    systemChip: {
      border: theme.appBorders.outline,
      borderRadius: 6,
      color: theme.palette.text.secondary,
      cursor: 'default',
      fontSize: theme.typography.pxToRem(10),
      fontWeight: 500,
      padding: theme.spacing(0, 0.75),
      whiteSpace: 'nowrap',
    },
  })
);

function ColumnItemView({
  column,
  columnIndex,
  dragHandleProps,
  isDragDisabled,
  isLocked,
  onColorChange,
  onDelete,
  onEdit,
  totalColumns,
}: ColumnItemViewProps): JSX.Element {
  const isDraggable = !isDragDisabled;
  const { classes: sharedClasses } = useSharedStyles();
  const { classes } = useColumnItemViewStyles();
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  // Nothing to reveal on hover means the row highlight would promise an interaction it doesn't have.
  const hasHoverAffordance = isDraggable || Boolean(onEdit) || Boolean(onDelete);

  return (
    <>
      <div
        className={clsx(
          sharedClasses.root,
          !isDraggable && sharedClasses.rootNotDraggable,
          !hasHoverAffordance && sharedClasses.rootStatic,
          hasHoverAffordance && classes.rootWithHover
        )}
      >
        {isDraggable && (
          <div {...dragHandleProps} className={classes.dragHandleWrapper}>
            <DragIcon className={classes.dragHandle} />
          </div>
        )}
        <div className={classes.columnIconContainer}>
          <ProgressiveColumnIcon
            category={column?.category}
            color={column?.color}
            index={columnIndex}
            total={totalColumns}
            onIconClick={isLocked ? undefined : e => setColorPickerAnchor(e.currentTarget)}
          />
          <Typography className={classes.name}>{column?.name}</Typography>
          {isLocked && <SystemColumnChip category={column?.category} />}
        </div>
        <Spacer />
        <div className={classes.hoverActions}>
          {onEdit && (
            <AlineTooltip arrow placement="top" title="Edit column">
              <IconButton size="small" onClick={onEdit}>
                <Pencil size={16} />
              </IconButton>
            </AlineTooltip>
          )}
          {onDelete && (
            <AlineTooltip arrow placement="top" title="Delete column">
              <IconButton size="small" onClick={onDelete}>
                <Trash2 size={16} />
              </IconButton>
            </AlineTooltip>
          )}
        </div>
      </div>
      <ColorPickerPopover
        anchorEl={colorPickerAnchor}
        open={Boolean(colorPickerAnchor)}
        selectedColor={column?.color}
        onClose={() => setColorPickerAnchor(null)}
        onColorSelect={color => {
          if (column) {
            onColorChange(color);
          }
        }}
      />
    </>
  );
}

function SystemColumnChip({ category }: { category?: Apollo.ColumnCategory }): JSX.Element {
  const { classes } = useColumnItemViewStyles();

  return (
    <AlineTooltip arrow placement="top" title={getSystemColumnTooltip(category)}>
      <Typography className={classes.systemChip}>System</Typography>
    </AlineTooltip>
  );
}

function getSystemColumnTooltip(category?: Apollo.ColumnCategory): string {
  if (category === Apollo.ColumnCategory.Triage)
    return 'The board manages this column, so its name and color are fixed. Delete it to turn triage off.';

  return 'The board manages this column, so its name and color are fixed. It comes back whenever a signing phase exists.';
}

interface ColumnItemProps {
  column: Column | null;
  /** 1-based position within the category group */
  columnIndex: number;
  onCancel: () => void;
  onSave: (column: Partial<Column>) => void;
  totalColumns: number;
  /** Category fallback when column is null (new column creation) */
  category?: Apollo.ColumnCategory;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isDragDisabled?: boolean;
  /** If true, component starts in editing mode (used for new column creation). Omitted when inside SortableColumnItem. */
  isEditing?: boolean;
  /** Board-managed column: no rename and no recolor. */
  isLocked?: boolean;
  onDelete?: () => void;
  onEditingChange?: (editing: boolean) => void;
}

export function ColumnItem({
  category,
  column,
  columnIndex,
  dragHandleProps,
  isDragDisabled,
  isEditing,
  isLocked,
  onCancel,
  onDelete,
  onEditingChange,
  onSave,
  totalColumns,
}: ColumnItemProps): JSX.Element {
  const focusColumnNameInput = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    // AlineInput forwards ref to InputBase root div — find the actual input
    const input = el instanceof HTMLInputElement ? el : el.querySelector('input');
    if (input) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, []);
  // Internal editing state for existing columns (when user clicks edit icon or row)
  const [isLocallyEditing, setIsLocallyEditing] = useState(false);
  const defaultColor =
    column?.color || (category ? getDefaultColumnColor(category) : DEFAULT_COLUMN_COLOR);
  const [editedColumn, setEditedColumn] = useState<Partial<Column>>({
    color: defaultColor,
    name: column?.name || '',
  });

  // Effective editing state: either controlled by parent (for new columns) or local state
  const effectiveIsEditing = Boolean(isEditing) || isLocallyEditing;

  if (effectiveIsEditing) {
    return (
      <ColumnItemEdit
        category={category}
        column={column}
        columnIndex={columnIndex}
        editedColumn={editedColumn}
        inputRef={focusColumnNameInput}
        totalColumns={totalColumns}
        onCancel={() => {
          setIsLocallyEditing(false);
          onEditingChange?.(false);
          setEditedColumn({
            color: defaultColor,
            name: column?.name || '',
          });
          onCancel();
        }}
        onColorChange={color => {
          setEditedColumn({ ...editedColumn, color });
        }}
        onNameChange={e => {
          setEditedColumn({ ...editedColumn, name: e.target.value });
        }}
        onSave={() => {
          if (editedColumn.name?.trim()) {
            onSave({ ...column, ...editedColumn });
            setIsLocallyEditing(false);
            onEditingChange?.(false);
          }
        }}
      />
    );
  }

  return (
    <ColumnItemView
      column={column}
      columnIndex={columnIndex}
      dragHandleProps={dragHandleProps}
      isDragDisabled={isDragDisabled}
      isLocked={isLocked}
      totalColumns={totalColumns}
      onColorChange={color => {
        if (column) {
          onSave({ ...column, color });
        }
      }}
      onDelete={onDelete}
      onEdit={
        isLocked
          ? undefined
          : () => {
              if (column) {
                setIsLocallyEditing(true);
                onEditingChange?.(true);
              }
            }
      }
    />
  );
}

const useColumnItemOverlayStyles = makeStyles()(theme => ({
  columnIconContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  dragHandle: {
    color: theme.palette.text.secondary,
    height: 16,
    marginRight: theme.spacing(1),
    width: 16,
  },
  dragHandleWrapper: {
    cursor: 'grabbing',
    display: 'flex',
  },
  name: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
  },
  root: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[8],
    cursor: 'grabbing',
    display: 'flex',
    padding: theme.spacing(1, 1.5),
  },
}));

interface ColumnItemOverlayProps {
  column: Column;
  columnIndex: number;
  totalColumns: number;
}

/**
 * Simplified column item for the drag overlay (ghost preview).
 * Shows elevated styling without interactive elements.
 */
export function ColumnItemOverlay({
  column,
  columnIndex,
  totalColumns,
}: ColumnItemOverlayProps): JSX.Element {
  const { classes } = useColumnItemOverlayStyles();

  return (
    <div className={classes.root}>
      <div className={classes.dragHandleWrapper}>
        <DragIcon className={classes.dragHandle} />
      </div>
      <div className={classes.columnIconContainer}>
        <ProgressiveColumnIcon
          category={column.category}
          color={column.color}
          iconSize={24}
          index={columnIndex}
          showBorder={false}
          total={totalColumns}
        />
        <Typography className={classes.name}>{column.name}</Typography>
      </div>
      <Spacer />
    </div>
  );
}
